/**
 * I18nProvider — locale state, message lookup, direction management (§14.2).
 *
 * - AR is the platform default (identity.yaml Accept-Language default).
 * - Switching locale re-stamps <html lang dir> so the WHOLE document mirrors,
 *   including portals (modals, toasts) rendered outside the React root.
 * - Locale preference is non-sensitive → localStorage is appropriate (§11.1).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { humaniseToken } from "./humanise";
import { catalogs, type MessageCatalog, type MessageKey } from "./messages";

export type Locale = "ar" | "en";
export type Direction = "rtl" | "ltr";

const LOCALE_KEY = "admin.locale";
const DEFAULT_LOCALE: Locale = "ar";

export function directionOf(locale: Locale): Direction {
  return locale === "ar" ? "rtl" : "ltr";
}

function readStoredLocale(): Locale {
  try {
    const raw = window.localStorage.getItem(LOCALE_KEY);
    return raw === "ar" || raw === "en" ? raw : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

function storeLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* preference persistence is best-effort */
  }
}

type Params = Readonly<Record<string, string | number>>;

export interface I18nContextValue {
  readonly locale: Locale;
  readonly dir: Direction;
  readonly setLocale: (locale: Locale) => void;
  /** Typed lookup — unknown keys are compile errors, never runtime blanks. */
  readonly t: (key: MessageKey, params?: Params) => string;
  readonly formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  readonly formatDate: (value: Date | string, options?: Intl.DateTimeFormatOptions) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

/**
 * Singular variant (M7-220): a key may declare a `<key>.one` sibling used when
 * `params.count` is exactly one item — "Cancel 1 order?", not "1 orders".
 * `count` usually arrives FORMATTED (Arabic-Indic digits, grouping), so it is
 * normalised before the CLDR plural category is taken. Deterministic, no
 * fallback guessing: no `.one` key → the base template as before.
 */
function singularOf(catalog: MessageCatalog, key: MessageKey, locale: Locale, params?: Params): string | undefined {
  const raw = params?.count;
  if (raw === undefined) return undefined;
  const n =
    typeof raw === "number"
      ? raw
      : Number(
          raw
            .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
            .replace(/[^\d.-]/g, ""),
        );
  if (!Number.isFinite(n) || new Intl.PluralRules(locale === "ar" ? "ar" : "en").select(n) !== "one") return undefined;
  return (catalog as Readonly<Record<string, string>>)[`${key}.one`];
}

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export interface I18nProviderProps {
  readonly children: ReactNode;
  /** Test override — production always restores the stored preference. */
  readonly initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? readStoredLocale());
  const dir = directionOf(locale);

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = dir;
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    storeLocale(next);
  }, []);

  const t = useCallback(
    (key: MessageKey, params?: Params) => {
      // The catalog type asserts `string`, but a DYNAMIC key cast to
      // MessageKey can miss at runtime — treat the lookup as possibly-absent.
      // `key` can also be undefined at runtime: a `Record<Enum, MessageKey>`
      // lookup that missed on a live value (typed as MessageKey by the map).
      const template = typeof key === "string" ? (catalogs[locale][key] as string | undefined) : undefined;
      if (template === undefined) {
        // A DYNAMIC key (`t(`domain.enum.${value}`)` cast to MessageKey) whose
        // value the catalog does not declare — a live enum value or error code
        // shipped after the catalog. Static keys are compile-checked and never
        // reach here. Never let a raw key, blank, or `String.replace` crash
        // reach the screen: degrade to readable copy (§14.2). The contract
        // raw-key sweep asserts every KNOWN value is declared, so this fires
        // only for a genuinely new value.
        if (import.meta.env.DEV) {
          console.warn(`[i18n] missing catalog key: "${key}" — rendering humanised fallback`);
        }
        return humaniseToken(key);
      }
      return interpolate(singularOf(catalogs[locale], key, locale, params) ?? template, params);
    },
    [locale],
  );

  const formatNumber = useCallback(
    (value: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(locale === "ar" ? "ar-IQ" : "en-US", options).format(value),
    [locale],
  );

  const formatDate = useCallback(
    (value: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions) => {
      // A wire date the browser cannot parse (or an absent one) is empty copy:
      // `Intl.DateTimeFormat.format(Invalid Date)` throws, and a thrown
      // RangeError in a meta line must never take a route down.
      const date = typeof value === "string" ? new Date(value) : value;
      if (date === null || date === undefined || Number.isNaN(date.getTime())) return "";
      return new Intl.DateTimeFormat(
        locale === "ar" ? "ar-IQ" : "en-US",
        options ?? { dateStyle: "medium", timeStyle: "short" },
      ).format(date);
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, dir, setLocale, t, formatNumber, formatDate }),
    [locale, dir, setLocale, t, formatNumber, formatDate],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
